import React from 'react';
import { Badge, Button, EmptyState, ErrorState, Table } from '@parsina/framework';
import { History, Pencil, Plus } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { EMPTY, formatUsd } from '../../lib/format';
import { num } from '../../lib/aiPricingView';
import { MODEL_PRICES_QUERY } from './queries';
import {
  BlockSkeleton, Code, CountCell, CsvExportButton, DetailDrawer, Fact, UsdCell, useEnumLabel,
} from './shared';

/**
 * One model: its facts, its append-only price history (newest first) and the
 * services that called it in the window. «افزودن نسخهٔ جدید» opens
 * `PriceDialog` — the ONLY way a price changes, so the history table is the
 * complete record of what any call was ever priced at.
 */
export default function ModelDrawer({ model, modelId, onClose, onAddPrice, onEdit, reloadKey }) {
  const { t, formatDate, formatNumber } = useTranslation();
  const usd = (v) => formatUsd(v, { formatNumber, t });
  const providerLabel = useEnumLabel('aiPricing.models.provider');
  const modalityLabel = useEnumLabel('aiPricing.models.modality');
  const statusLabel = useEnumLabel('aiPricing.models.status');
  const sourceLabel = useEnumLabel('aiPricing.models.drawer.source');

  const [prices, setPrices] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(MODEL_PRICES_QUERY, { modelId })
      .then((d) => setPrices(d?.aiModelPrices || []))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [modelId, t]);

  React.useEffect(() => { load(); }, [load, reloadKey]);

  // Newest first: the version in force is the one the operator opened the
  // drawer to check.
  const history = React.useMemo(
    () => [...(prices || [])].sort((a, b) => String(b.effectiveFrom).localeCompare(String(a.effectiveFrom)) || Number(b.id) - Number(a.id)),
    [prices],
  );

  const csvColumns = [
    { key: 'effectiveFrom', header: t('aiPricing.models.drawer.columns.from'), value: (p) => p.effectiveFrom },
    { key: 'inputUsdPerM', header: t('aiPricing.models.drawer.columns.input'), value: (p) => p.inputUsdPerM },
    { key: 'outputUsdPerM', header: t('aiPricing.models.drawer.columns.output'), value: (p) => p.outputUsdPerM },
    { key: 'cachedInputUsdPerM', header: t('aiPricing.models.drawer.columns.cached'), value: (p) => p.cachedInputUsdPerM },
    { key: 'audio', header: t('aiPricing.models.drawer.columns.audio'), value: (p) => p.audioUsdPerMinute ?? p.audioInputUsdPerM },
    { key: 'requestUsd', header: t('aiPricing.models.drawer.columns.request'), value: (p) => p.requestUsd },
    { key: 'source', header: t('aiPricing.models.drawer.columns.source'), value: (p) => p.source },
    { key: 'createdBy', header: t('aiPricing.models.drawer.columns.by'), value: (p) => p.createdBy },
  ];

  const audioOf = (p) => {
    if (num(p.audioUsdPerMinute) != null) return t('aiPricing.models.audioPerMinute', { value: usd(p.audioUsdPerMinute) });
    if (num(p.audioInputUsdPerM) != null) return t('aiPricing.models.audioPerM', { value: usd(p.audioInputUsdPerM) });
    return EMPTY;
  };

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={t('aiPricing.models.drawer.title')}
      sub={<Code>{modelId}</Code>}
      data-testid="ai-models-drawer"
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onAddPrice} data-testid="ai-models-drawer-add-price">
            <Plus size={16} aria-hidden />
            {t('aiPricing.models.drawer.addVersion')}
          </Button>
          {model ? (
            <Button variant="outline" onClick={onEdit} data-testid="ai-models-drawer-edit">
              <Pencil size={16} aria-hidden />
              {t('aiPricing.models.dialog.editTitle')}
            </Button>
          ) : null}
        </div>

        {model ? (
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <Fact label={t('aiPricing.models.columns.provider')} value={providerLabel(model.provider)} />
            <Fact label={t('aiPricing.models.columns.status')} value={<Badge variant={model.status === 'unknown' ? 'destructive' : model.status === 'active' ? 'success' : 'outline'}>{statusLabel(model.status)}</Badge>} />
            <Fact label={t('aiPricing.models.dialog.displayName')} value={model.displayName} />
            <Fact
              label={t('aiPricing.models.columns.modalities')}
              value={(model.modalities || []).length ? (
                <span className="inline-flex flex-wrap gap-1">
                  {model.modalities.map((m) => <Badge key={m} variant="outline">{modalityLabel(m)}</Badge>)}
                </span>
              ) : EMPTY}
            />
            <Fact label={t('aiPricing.models.columns.calls')} value={<CountCell value={model.callsInWindow} />} />
            <Fact label={t('aiPricing.models.columns.cost')} value={<UsdCell value={model.costUsdInWindow} />} />
            {model.notes ? <div className="col-span-2"><Fact label={t('aiPricing.models.dialog.notes')} value={<span dir="auto">{model.notes}</span>} /></div> : null}
          </dl>
        ) : null}

        <section>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="inline-flex items-center gap-1.5 text-sm font-bold text-foreground">
              <History size={16} aria-hidden />
              {t('aiPricing.models.drawer.history')}
            </h3>
            <CsvExportButton columns={csvColumns} rows={history} data-testid="ai-models-drawer-csv" />
          </div>
          {loading ? (
            <div data-state="loading" role="status" aria-busy="true" aria-label={t('aiPricing.models.drawer.loading')}>
              <BlockSkeleton lines={3} />
            </div>
          ) : error ? (
            <ErrorState
              title={t('aiPricing.models.drawer.error')}
              description={error}
              onRetry={load}
              retryLabel={t('common.actions.retry')}
              data-testid="ai-models-drawer-error"
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border">
              <Table
                dense
                ariaLabel={t('aiPricing.models.drawer.historyAria')}
                rows={history}
                rowKey={(p) => p.id}
                columns={[
                  { key: 'from', header: t('aiPricing.models.drawer.columns.from'), render: (p) => formatDate(p.effectiveFrom) },
                  { key: 'input', header: t('aiPricing.models.drawer.columns.input'), align: 'end', render: (p) => <UsdCell value={p.inputUsdPerM} /> },
                  { key: 'output', header: t('aiPricing.models.drawer.columns.output'), align: 'end', render: (p) => <UsdCell value={p.outputUsdPerM} /> },
                  { key: 'cached', header: t('aiPricing.models.drawer.columns.cached'), align: 'end', render: (p) => <UsdCell value={p.cachedInputUsdPerM} /> },
                  { key: 'audio', header: t('aiPricing.models.drawer.columns.audio'), align: 'end', render: (p) => <span className="tabular-nums">{audioOf(p)}</span> },
                  { key: 'request', header: t('aiPricing.models.drawer.columns.request'), align: 'end', render: (p) => <UsdCell value={p.requestUsd} /> },
                  { key: 'source', header: t('aiPricing.models.drawer.columns.source'), render: (p) => <Badge variant="outline">{sourceLabel(p.source)}</Badge> },
                  { key: 'by', header: t('aiPricing.models.drawer.columns.by'), render: (p) => (p.createdBy ? <Code title={p.createdBy}>{String(p.createdBy).slice(0, 8)}</Code> : EMPTY) },
                ]}
                empty={(
                  <EmptyState
                    icon={<History size={28} strokeWidth={1.5} aria-hidden />}
                    title={t('aiPricing.models.drawer.historyEmpty')}
                    data-testid="ai-models-drawer-history-empty"
                  />
                )}
                data-testid="ai-models-drawer-history"
              />
            </div>
          )}
        </section>

        <section>
          <h3 className="mb-2 text-sm font-bold text-foreground">{t('aiPricing.models.drawer.services')}</h3>
          {model?.usedByServices?.length ? (
            <ul className="flex flex-wrap gap-1.5" data-testid="ai-models-drawer-services">
              {model.usedByServices.map((key) => (
                <li key={key}><Badge variant="outline"><Code>{key}</Code></Badge></li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('aiPricing.models.drawer.servicesEmpty')}</p>
          )}
        </section>
      </div>
    </DetailDrawer>
  );
}

